/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useState } from 'react';
import {
  IconButton, Box, Typography, InputBase, TextareaAutosize, Stack,
} from '@mui/material';
import { Variant as TypographyVariant } from '@mui/material/styles/createTypography';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import theme from '../theme';
import ConfirmModal from './modals/confirm-modal';
import Tooltip from './info-elements/tooltip';

export type EditableTypes = 'title' | 'subtitle';

interface Props {
  value?: string;
  typographyVariant?: TypographyVariant;
  type?: 'text' | 'textarea';
  isEditing: boolean;
  emptyText?: string;
  onSave?: (type: EditableTypes, value?: string) => any;
  onStartEditing?: () => void;
  onStopEditing?: () => void;
  truncate?: boolean;
  errorMessage?: string;
  onFocus?: () => void;
}

export default function InlineEditable({
  value: initialValue,
  emptyText,
  onSave,
  onStartEditing,
  onStopEditing,
  isEditing,
  typographyVariant = 'h1',
  type = 'text',
  truncate = false,
  errorMessage = '',
  onFocus,
}: Props) {
  const [displayEditButton, setDisplayEditButton] = useState(false);
  const [discardChangesDialogOpen, setDiscardChangesDialogOpen] = useState(false);
  const [previousValue, setPreviousValue] = useState(initialValue);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);
  const showEmptyState = !value && !!emptyText;

  React.useEffect(() => () => stopEditing(), []);
  const handleSave = async () => {
    const editableType = type === 'text' ? 'title' : 'subtitle';

    if (value !== previousValue) {
      onSave?.(editableType, value?.trim());
    } else {
      stopEditing();
    }
  };

  const handleCancel = () => {
    if (value !== previousValue) {
      showDiscardChangesDialog();
    } else {
      stopEditing();
    }
  };

  const startEditing = () => {
    setPreviousValue(value);
    onStartEditing?.();
  };

  const stopEditing = (saved = false) => {
    if (!saved) {
      setValue(previousValue);
    }
    hideDiscardChangesDialog();
    onStopEditing?.();
  };

  const showEditButton = () => {
    setDisplayEditButton(true);
  };

  const hideEditButton = () => {
    setDisplayEditButton(false);
  };

  const showDiscardChangesDialog = () => {
    setDiscardChangesDialogOpen(true);
  };

  const hideDiscardChangesDialog = () => {
    setDiscardChangesDialogOpen(false);
  };

  const handleInputChange = (event) => {
    setValue(event.target.value);
  };

  const SaveButton = () => (
    <IconButton
      color="secondary"
      size="small"
      onClick={handleSave}
      data-testid="inline-edit-save-button"
    >
      <CheckIcon />
    </IconButton>
  );

  const CancelButton = () => (
    <IconButton size="small" onClick={handleCancel} sx={{ mr: 1 }} data-testid="inline-edit-cancel-button">
      <CloseIcon />
    </IconButton>
  );

  const EditButton = () => (
    <IconButton
      size="small"
      onClick={startEditing}
      data-testid="inline-edit-button"
      sx={editButtonStyles(displayEditButton, showEmptyState)}
    >
      <EditIcon />
    </IconButton>
  );

  const EditableTextInput = () => (
    <InputBase
      inputRef={inputRef}
      sx={editableTextInputStyles(typographyVariant)}
      inputProps={{ style: { height: 'auto', padding: '0' }, 'data-testid': 'inline-edit-input' }}
      type={type}
      value={value}
      autoFocus
      onFocus={onFocus}
      onChange={handleInputChange}
    />
  );

  const EditableTextareaInput = () => (
    <TextareaAutosize
      ref={inputRef}
      style={editableTextareaStyles(typographyVariant)}
      value={value}
      autoFocus
      onChange={handleInputChange}
    />
  );

  const DisplayComponent = () => (
    <Box
      sx={displayContainerStyles(showEmptyState)}
      onMouseLeave={hideEditButton}
      onMouseEnter={showEditButton}
      data-testid="inline-edit-display"
    >
      <Tooltip title={value} forceHoverListener={truncate}>
        <Typography
          variant={typographyVariant}
          {...(truncate && {
            sx: truncateStyle,
          })}
        >
          {value || emptyText}
        </Typography>
      </Tooltip>
      <Box sx={displayActionsContainerStyles(type)}>
        <EditButton />
      </Box>
    </Box>
  );

  const EditingComponent = () => (
    <Stack
      spacing={1}
      sx={{ width: '100%' }}
    >
      <Box sx={editingContainerStyles(!!errorMessage)}>
        {type === 'text' && EditableTextInput()}
        {type === 'textarea' && EditableTextareaInput()}
        <Box sx={editingActionsContainerStyles(type)}>
          {CancelButton()}
          {SaveButton()}
        </Box>
      </Box>
      {!!errorMessage
        && (
          <Typography variant="caption" sx={{ color: '#FF3B30', paddingLeft: '14px' }}>
            {errorMessage}
          </Typography>
        )}
    </Stack>
  );

  const ConfirmationDialog = () => (
    <ConfirmModal
      title="Discard changes?"
      positive="Confirm"
      open={discardChangesDialogOpen}
      onPositive={() => stopEditing(false)}
      onNegative={hideDiscardChangesDialog}
      data-testid="inline-edit-cancel-dialog"
    />
  );

  return (
    <Box sx={containerStyles(isEditing)}>
      {!isEditing && DisplayComponent()}
      {isEditing && EditingComponent()}
      {ConfirmationDialog()}
    </Box>
  );
}

const containerStyles = (editing: boolean) => ({
  display: 'flex',
  width: editing ? '100%' : 'auto',
  maxWidth: '100%',
  flex: 1,
});

const displayContainerStyles = (emptyState: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  maxWidth: '100%',
  p: 1,
  border: 1,
  borderColor: 'transparent',
  color: emptyState ? 'secondary.main' : 'primary.main',
});

const displayActionsContainerStyles = (type: string) => ({
  display: 'flex',
  height: '100%',
  alignItems: type === 'textarea' ? 'flex-start' : 'center',
});

const editingContainerStyles = (error) => ({
  display: 'flex',
  alignItems: 'center',
  border: 1,
  borderColor: error ? 'error.main' : 'secondary.main',
  p: 1,
  width: '100%',
  borderRadius: 1,
});

const editingActionsContainerStyles = (type: string) => ({
  alignItems: type === 'textarea' ? 'flex-start' : 'center',
  display: 'flex',
  height: '100%',
});

const editableTextInputStyles = (typographyVariant: string) => ({
  typography: typographyVariant,
  p: 0,
  width: '100%',
});

const editableTextareaStyles = (typographyVariant: string) => ({
  ...theme.typography[typographyVariant],
  width: '100%',
  resize: 'none',
  overflow: 'hidden',
  border: 'none',
  outline: 'none',
  maxWidth: 'md',
  background: 'transparent',
});

const editButtonStyles = (displayEditButton: boolean, showEmptyState: boolean) => ({
  mx: 1,
  visibility: displayEditButton ? 'visible' : 'hidden',
  color: showEmptyState ? 'secondary.main' : 'primary.main',
});

const truncateStyle = {
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
};
